import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";

const LS_KEY = "fp_authority_checklist";
const EMPTY = { checked: {}, mc_number: "", dot_number: "", mc_applied_at: "", quotes: [] };

const readLocal = () => {
  try {
    return { ...EMPTY, ...(JSON.parse(localStorage.getItem(LS_KEY) || "{}") || {}) };
  } catch {
    return { ...EMPTY };
  }
};

const hasData = (s) => Object.keys(s.checked || {}).length > 0 || s.mc_number || s.dot_number || s.mc_applied_at || (s.quotes || []).length > 0;
const splitServer = (data) => {
  const { reminders, watch, renewal, status, updated_at, first_load_plan_sent_at, ...rest } = data || {};
  return { state: { ...EMPTY, ...rest }, meta: { reminders: reminders || null, watch: watch || null, renewal: renewal || null, firstLoadPlanSentAt: first_load_plan_sent_at || null } };
};

// Anonymous → localStorage only. Logged in → server is source of truth (local copy migrated up once).
export function useAuthorityProgress(user) {
  const [state, setState] = useState(readLocal);
  const [synced, setSynced] = useState(false);
  const [meta, setMeta] = useState({ reminders: null, watch: null, renewal: null, firstLoadPlanSentAt: null });
  const [checking, setChecking] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    api.get("/account/authority-checklist").then(({ data }) => {
      if (!alive) return;
      const { state: server, meta: m } = splitServer(data);
      const local = readLocal();
      const next = hasData(server) || !hasData(local) ? server : local;
      setState(next);
      setMeta(m);
      localStorage.setItem(LS_KEY, JSON.stringify(next));
      if (!hasData(server) && hasData(local)) {
        api.put("/account/authority-checklist", local).then(({ data: d }) => setMeta(splitServer(d).meta)).catch(() => {});
      }
      setSynced(true);
    }).catch(() => setSynced(false));
    return () => { alive = false; };
  }, [user]);

  const update = useCallback((patch) => {
    setState((prev) => {
      const next = typeof patch === "function" ? patch(prev) : { ...prev, ...patch };
      localStorage.setItem(LS_KEY, JSON.stringify(next));
      if (user) {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
          api.put("/account/authority-checklist", next)
            .then(({ data }) => { setMeta(splitServer(data).meta); setSynced(true); })
            .catch(() => setSynced(false));
        }, 500);
      }
      return next;
    });
  }, [user]);

  const toggle = useCallback((id) => update((prev) => {
    const checked = { ...prev.checked };
    if (checked[id]) delete checked[id]; else checked[id] = new Date().toISOString();
    return { ...prev, checked };
  }), [update]);

  const reset = useCallback(() => update({ ...EMPTY }), [update]);

  const setQuotes = useCallback((quotes) => update((prev) => {
    const checked = { ...prev.checked };
    const now = new Date().toISOString();
    if (quotes.length >= 3 && !checked["insurance-quotes"]) checked["insurance-quotes"] = now;
    if (quotes.some((q) => q.bound)) {
      if (!checked["insurance-quotes"]) checked["insurance-quotes"] = now;
      if (!checked["insurance-filed"]) checked["insurance-filed"] = now;
    }
    return { ...prev, quotes, checked };
  }), [update]);

  const markDone = useCallback((id) => update((prev) => (
    prev.checked[id] ? prev : { ...prev, checked: { ...prev.checked, [id]: new Date().toISOString() } }
  )), [update]);

  const checkNow = useCallback(async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/account/authority-checklist/check-now");
      setMeta((m) => ({ ...m, watch: data }));
      return { ok: true, watch: data };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || "Lookup failed — try again" };
    } finally { setChecking(false); }
  }, []);

  return { state, update, toggle, reset, synced, reminders: meta.reminders, watch: meta.watch, renewal: meta.renewal, firstLoadPlanSentAt: meta.firstLoadPlanSentAt, checkNow, checking, setQuotes, markDone };
}
